import { useEffect, useMemo, useState } from 'react';
import { Button, Select } from 'antd';
import { ArrowLeftOutlined, ArrowRightOutlined, EnvironmentOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
    getAvailableDenoms,
    getDenomPlanLabel,
    getProductDescription,
    getProductDisplayName,
    getProductLogo,
    isEsimProduct,
    type CatalogProduct,
} from '../catalog';
import { formatNumber } from '../../../i18n/format';

type ProductDetailProps = {
    product: CatalogProduct;
    regionLabel?: string;
    onBack: () => void;
    onReview: (amount: number, quantity: number) => void;
};

function formatCategoryLabel(value?: string) {
    if (!value) return '';

    return value
        .replace(/[_-]+/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase())
        .trim();
}

function ProductDetail({ product, regionLabel: regionLabelProp, onBack, onReview }: ProductDetailProps) {
    const { t, i18n } = useTranslation('explore');
    const language = i18n.resolvedLanguage || i18n.language;
    const denoms = useMemo(() => getAvailableDenoms(product), [product]);
    const showPlanLabel = isEsimProduct(product);
    const productName = getProductDisplayName(product);
    const description = getProductDescription(product, language);
    const logoUrl = getProductLogo(product);
    const regionLabel = regionLabelProp || product.regionName || product.country || t('filters.allRegions');
    const categoryLabel = formatCategoryLabel(product.category);
    const currency = denoms[0]?.currency || product.currency || '';
    const isInstant = product.deliveryType === 'INSTANT';
    const [amount, setAmount] = useState(denoms[1]?.value ?? denoms[0]?.value ?? 0);
    const [quantity, setQuantity] = useState(1);

    useEffect(() => {
        setAmount(denoms[1]?.value ?? denoms[0]?.value ?? 0);
        setQuantity(1);
    }, [product.id, denoms]);

    const formatDenomLabel = (value: number) => {
        return `${formatNumber(value)} ${currency}`.trim();
    };

    return (
        <div className="explore-detail">
            <button type="button" className="explore-detail__back" onClick={onBack}>
                <ArrowLeftOutlined />
                {t('actions.allGiftCards')}
            </button>

            <div className="explore-detail__layout">
                <section className="explore-detail__stage">
                    <div className="explore-detail__glow" aria-hidden />
                    <div className="explore-detail__brand">
                        <div
                            className={`explore-detail__logo${
                                logoUrl ? ' explore-detail__logo--image' : ''
                            }`}
                        >
                            {logoUrl ? (
                                <img src={logoUrl} alt="" />
                            ) : (
                                <span>{productName.charAt(0)}</span>
                            )}
                        </div>
                        <div className="explore-detail__intro">
                            <div className="explore-detail__tags">
                                {isInstant ? (
                                    <span className="explore-detail__tag">{t('product.instant')}</span>
                                ) : null}
                                {categoryLabel ? (
                                    <span className="explore-detail__tag explore-detail__tag--quiet">
                                        {categoryLabel}
                                    </span>
                                ) : null}
                            </div>
                            <h1>{productName}</h1>
                            <p className="explore-detail__region">
                                <EnvironmentOutlined />
                                {regionLabel}
                            </p>
                        </div>
                    </div>

                    {description ? (
                        <p className="explore-detail__desc">{description}</p>
                    ) : (
                        <p className="explore-detail__desc">
                            {t('product.fallbackDescription', {
                                productName,
                                region: regionLabel,
                            })}
                        </p>
                    )}

                    <dl className="explore-detail__facts">
                        <div>
                            <dt>{t('product.useIn')}</dt>
                            <dd>{regionLabel}</dd>
                        </div>
                        <div>
                            <dt>{t('product.delivery')}</dt>
                            <dd>
                                {isInstant
                                    ? t('product.instantDigitalCode')
                                    : t('product.digitalDelivery')}
                            </dd>
                        </div>
                        <div>
                            <dt>{t('purchase.payWith')}</dt>
                            <dd>{t('purchase.apcCredits')}</dd>
                        </div>
                    </dl>
                </section>

                <aside className="explore-detail__panel">
                    <h2>{t('product.chooseAmount')}</h2>
                    <p>{t('product.chooseAmountHint')}</p>
                    <div className="explore-detail__denoms">
                        {denoms.map((item) => {
                            const planLabel = showPlanLabel ? getDenomPlanLabel(item, language) : '';
                            return (
                                <button
                                    key={item.id}
                                    type="button"
                                    className={item.value === amount ? 'active' : ''}
                                    aria-pressed={item.value === amount}
                                    onClick={() => setAmount(item.value)}
                                >
                                    <strong>{formatDenomLabel(item.value)}</strong>
                                    {planLabel ? <span>{planLabel}</span> : null}
                                </button>
                            );
                        })}
                    </div>
                    <label className="explore-detail__quantity">
                        <span>{t('purchase.quantity')}</span>
                        <Select
                            className="explore-detail__quantity-select"
                            popupClassName="explore-detail__quantity-dropdown"
                            aria-label={t('purchase.quantity')}
                            value={quantity}
                            onChange={setQuantity}
                            options={Array.from({ length: 10 }, (_, index) => ({
                                value: index + 1,
                                label: formatNumber(index + 1),
                            }))}
                        />
                    </label>
                    <Button
                        type="primary"
                        className="explore-detail__submit"
                        onClick={() => onReview(amount, quantity)}
                    >
                        {t('actions.reviewPurchase')}
                        <ArrowRightOutlined />
                    </Button>
                </aside>
            </div>
        </div>
    );
}

export default ProductDetail;
